import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api';
import './estilodashuser.css';

function formatDate(value, locale) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(locale);
}

function getStatusNumber(value) {
  return Number(value) === 1 ? 1 : 0;
}

function getStatusLabel(value, t) {
  return getStatusNumber(value) === 1 ? t('common.active') : t('common.inactive');
}

function getTipoLabel(tipo) {
  const t = String(tipo || '').toLowerCase();
  if (t === 'real') return 'Real';
  if (t === 'demo') return 'Demo';
  return tipo || '—';
}

function getProdutoDisplay(licenca, fallback) {
  const raw = String(
    licenca?.produto_nome ||
    licenca?.nome_produto ||
    licenca?.produto_codigo ||
    licenca?.codigo_plano ||
    ''
  ).trim();

  const normalized = raw.toLowerCase();

  if (
    normalized === 'ea' ||
    normalized.includes('quantum trades ea') ||
    normalized.includes('quantumtrades ea') ||
    normalized.includes('quantum trades ia') ||
    normalized.includes('quantumtrades ia')
  ) {
    return 'Quantum Trades IA';
  }

  if (
    normalized === 'bias' ||
    normalized.includes('quantumtrades bias') ||
    normalized.includes('quantum trades bias')
  ) {
    return 'Quantum Trades Bias';
  }

  if (
    normalized === 'ragnarok' ||
    normalized.includes('ragnarok') ||
    normalized.includes('ragnarock')
  ) {
    return 'QuantumTrades Ragnarok';
  }

  return raw || fallback;
}

export default function ListarLicencas({ userId }) {
  const { t, i18n } = useTranslation();
  const [licencas, setLicencas] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [removendoId, setRemovendoId] = useState('');
  const locale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'pt-BR';

  useEffect(() => {
    if (userId) {
      carregarDados();
    }
  }, [userId]);

  const carregarDados = async () => {
    try {
      setCarregando(true);
      setMensagem('');
      setTipoMensagem('');

      const [respLicencas, respAssinaturas] = await Promise.all([
        api.get('/api/minhas-licencas'),
        api.get('/api/minhas-assinaturas'),
      ]);

      setLicencas(respLicencas.data || []);
      setAssinaturas(respAssinaturas.data || []);
    } catch (error) {
      console.error('Erro ao carregar licenças do cliente:', error);
      setMensagem(t('dashboard.licenses.loadError'));
      setTipoMensagem('erro');
    } finally {
      setCarregando(false);
    }
  };

  const resumo = useMemo(() => {
    const licencasAtivas = licencas.filter((l) => getStatusNumber(l.status) === 1);
    const licencasDemo = licencas.filter(
      (l) => String(l.tipo || '').toLowerCase() === 'demo'
    ).length;
    const licencasReal = licencas.filter(
      (l) => String(l.tipo || '').toLowerCase() === 'real'
    ).length;

    const assinaturasAtivas = assinaturas.filter((a) => Number(a.status) === 1);

    const slotsTotais = assinaturasAtivas.reduce(
      (acc, item) => acc + Number(item.slots_contratados || 0),
      0
    );

    const slotsDisponiveis = Math.max(slotsTotais - licencasAtivas.length, 0);

    return {
      totalLicencas: licencas.length,
      licencasAtivas: licencasAtivas.length,
      licencasDemo,
      licencasReal,
      slotsTotais,
      slotsDisponiveis,
    };
  }, [licencas, assinaturas]);

  const resumoAssinaturas = useMemo(() => {
    const ativas = assinaturas.filter((a) => Number(a.status) === 1);

    if (!ativas.length) {
      return t('dashboard.licenses.noSubscription');
    }

    return ativas
      .map((a) => {
        const nome = a.nome_plano || a.codigo_plano || t('dashboard.account.subscription');
        return `${nome} (${a.tier})`;
      })
      .join(' • ');
  }, [assinaturas, t]);

  const removerLicenca = async (licenca) => {
    const conta = licenca?.conta || '—';
    const confirmou = window.confirm(
      t('dashboard.licenses.confirm', { account: conta })
    );

    if (!confirmou) return;

    try {
      setRemovendoId(String(licenca.id));
      setMensagem('');
      setTipoMensagem('');

      await api.delete(`/api/minhas-licencas/${licenca.id}`);
      setLicencas((atuais) =>
        atuais.filter((item) => String(item.id) !== String(licenca.id))
      );
      setMensagem(t('dashboard.licenses.removed'));
      setTipoMensagem('sucesso');
    } catch (error) {
      console.error('Erro ao remover licença:', error);
      setMensagem(
        i18n.resolvedLanguage === 'en'
          ? t('dashboard.licenses.removeError')
          : error.response?.data?.message || t('dashboard.licenses.removeError')
      );
      setTipoMensagem('erro');
    } finally {
      setRemovendoId('');
    }
  };

  return (
    <div className="lic-page">
      <div className="lic-card">
        <div className="lic-header">
          <div className="lic-title">
            <span className="lic-ico">📜</span>
            <div>
              <h3 className="lic-h3">{t('dashboard.licenses.title')}</h3>
              <div className="lic-subtitle">{t('dashboard.licenses.subtitle')}</div>
            </div>
          </div>

          <button type="button" className="lic-btn-outline qt-btn qt-btn-secondary" onClick={carregarDados}>
            {t('dashboard.licenses.reload')}
          </button>
        </div>

        <div className="cliente-help-box" style={{ marginBottom: 14 }}>
          <strong>{t('dashboard.licenses.helpTitle')}</strong> {t('dashboard.licenses.help')}
        </div>

        <div className="lic-kpis">
          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.licenses.registered')}</div>
            <div className="lic-kpi-value">{resumo.totalLicencas}</div>
          </div>

          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.licenses.active')}</div>
            <div className="lic-kpi-value">{resumo.licencasAtivas}</div>
          </div>

          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.licenses.real')}</div>
            <div className="lic-kpi-value">{resumo.licencasReal}</div>
          </div>

          <div className="lic-kpi">
            <div className="lic-kpi-label">{t('dashboard.licenses.demo')}</div>
            <div className="lic-kpi-value">{resumo.licencasDemo}</div>
          </div>
        </div>

        <div className="summary-panel">
          <div className="summary-panel-title">{t('dashboard.licenses.summary')}</div>
          <div className="summary-panel-main">{resumoAssinaturas}</div>
          <div className="summary-panel-sub">
            {t('dashboard.licenses.releasedSlots')}: <strong>{resumo.slotsTotais}</strong> •{' '}
            {t('dashboard.licenses.availableNow')}: <strong>{resumo.slotsDisponiveis}</strong>
          </div>
        </div>

        {mensagem && (
          <div
            className={
              tipoMensagem === 'erro'
                ? 'alert-box alert-error'
                : 'alert-box alert-success'
            }
          >
            {mensagem}
          </div>
        )}

        {carregando && <p className="lic-loading">{t('dashboard.licenses.loading')}</p>}

        <div className="lic-table-wrap">
          <table className="lic-table">
            <thead>
              <tr>
                <th>{t('dashboard.licenses.product')}</th>
                <th>{t('dashboard.licenses.account')}</th>
                <th>{t('dashboard.licenses.type')}</th>
                <th>{t('dashboard.licenses.status')}</th>
                <th>{t('dashboard.licenses.created')}</th>
                <th>{t('dashboard.licenses.expires')}</th>
                <th>{t('dashboard.licenses.actions')}</th>
              </tr>
            </thead>

            <tbody>
              {!carregando && licencas.length === 0 ? (
                <tr>
                  <td colSpan="7">{t('dashboard.licenses.empty')}</td>
                </tr>
              ) : (
                licencas.map((licenca) => (
                  <tr key={licenca.id}>
                    <td>
                      <div className="lic-produto-nome">
                        {getProdutoDisplay(licenca, t('dashboard.addLicense.productFallback'))}
                      </div>
                    </td>

                    <td className="lic-mono">{licenca.conta || '—'}</td>
                    <td>{getTipoLabel(licenca.tipo)}</td>
                    <td>
                      <span
                        className={
                          getStatusNumber(licenca.status) === 1
                            ? 'qt-badge qt-badge-success'
                            : 'qt-badge qt-badge-danger'
                        }
                      >
                        {getStatusLabel(licenca.status, t)}
                      </span>
                    </td>

                    <td>{formatDate(licenca.created_on, locale)}</td>
                    <td>{formatDate(licenca.date_exp, locale)}</td>
                    <td>
                      <div className="lic-actions">
                        <button
                          type="button"
                          className="lic-btn-danger qt-btn"
                          onClick={() => removerLicenca(licenca)}
                          disabled={removendoId === String(licenca.id)}
                          title={t('dashboard.licenses.removeTitle')}
                        >
                          {removendoId === String(licenca.id) ? t('dashboard.licenses.removing') : t('dashboard.licenses.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="lic-footnote">
          {t('dashboard.licenses.footnote')}
        </div>
      </div>
    </div>
  );
}
